import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Form } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { LoaderBar } from 'components'
import { useNotification } from 'hooks/toast'
import { TemplateService } from 'utility/services/template'
import { MasterAnswersService } from 'utility/services/master-answers'
import {
    EmptyHint,
    InfoBanner,
    PreviewCard,
    PreviewItem,
    PreviewTitle,
    SettingsCard,
    SettingsCardBody,
    SettingsCardHeader,
    SourceToggle,
    TemplateQuestionCard,
    ToggleSwitch,
    TqHeader,
    TqName,
    TqTypeBadge,
    WarnBanner,
} from './styles'
import {
    extractMultiChoiceQuestions,
    formatInlineOptions,
} from './template-editor-utils'
import { getApiErrorMessage, normalizeItems, normalizeLists } from './utils'

export const TemplateEditorTab = ({ companyId }) => {
    const { successToast, errorToast } = useNotification()
    const errorToastRef = useRef(errorToast)
    const successToastRef = useRef(successToast)
    errorToastRef.current = errorToast
    successToastRef.current = successToast

    const [templates, setTemplates] = useState([])
    const [templatesLoading, setTemplatesLoading] = useState(true)
    const [templateId, setTemplateId] = useState(null)
    const [templateLoading, setTemplateLoading] = useState(false)
    const [questions, setQuestions] = useState([])
    const [selectedQuestionId, setSelectedQuestionId] = useState(null)

    const [masterLists, setMasterLists] = useState([])
    const [listsLoading, setListsLoading] = useState(true)
    const [previewItems, setPreviewItems] = useState([])
    const [previewLoading, setPreviewLoading] = useState(false)
    const [assigning, setAssigning] = useState(false)

    const [useMaster, setUseMaster] = useState(false)
    const [masterListId, setMasterListId] = useState(null)
    const [displayType, setDisplayType] = useState('radio')
    const [allowOther, setAllowOther] = useState(false)

    const selectedQuestion = questions.find(
        q => q.questionId === selectedQuestionId,
    )

    const loadTemplates = useCallback(async () => {
        setTemplatesLoading(true)
        try {
            const result = await TemplateService.templateList({
                PageNumber: 1,
                PageSize: 5000,
                SearchText: '',
            })
            const items = (result?.Items || []).map(t => ({
                key: t.Id,
                value: t.Id,
                text: t.TemplateName || t.Name,
                templateUniqueId: t.TemplateUniqueId ?? t.Id,
            }))
            setTemplates(items)
        } catch (err) {
            errorToastRef.current(getApiErrorMessage(err))
            setTemplates([])
        } finally {
            setTemplatesLoading(false)
        }
    }, [])

    const loadMasterLists = useCallback(async () => {
        setListsLoading(true)
        try {
            const data = await MasterAnswersService.getListsByCompany(companyId)
            setMasterLists(
                normalizeLists(data).filter(l => l.isActive),
            )
        } catch (err) {
            errorToastRef.current(getApiErrorMessage(err))
            setMasterLists([])
        } finally {
            setListsLoading(false)
        }
    }, [companyId])

    useEffect(() => {
        loadTemplates()
        loadMasterLists()
    }, [loadTemplates, loadMasterLists])

    const applyQuestionSelection = useCallback(
        question => {
            if (!question) {
                setUseMaster(false)
                setMasterListId(null)
                return
            }
            const linked = question.masterListId
            setUseMaster(!!linked)
            setMasterListId(linked || null)
            const ds = question.displaySetting || {}
            if (ds.IsListBox || ds.isListBox) setDisplayType('listbox')
            else setDisplayType('radio')
            setAllowOther(
                !!(ds.AllowOtherResponse ?? ds.allowOtherResponse),
            )
        },
        [],
    )

    const loadTemplateQuestions = useCallback(
        async id => {
            if (!id) {
                setQuestions([])
                setSelectedQuestionId(null)
                return
            }
            setTemplateLoading(true)
            try {
                const detail = await TemplateService.getTemplateDetails(id)
                const extracted = extractMultiChoiceQuestions(detail)
                setQuestions(extracted)
                const first = extracted[0]
                setSelectedQuestionId(first?.questionId ?? null)
                applyQuestionSelection(first)
            } catch (err) {
                errorToastRef.current(getApiErrorMessage(err))
                setQuestions([])
            } finally {
                setTemplateLoading(false)
            }
        },
        [applyQuestionSelection],
    )

    const loadPreview = useCallback(async listId => {
        if (!listId) {
            setPreviewItems([])
            return
        }
        setPreviewLoading(true)
        try {
            const data = await MasterAnswersService.getItems(listId)
            setPreviewItems(
                normalizeItems(data).filter(i => i.isActive),
            )
        } catch (err) {
            errorToastRef.current(getApiErrorMessage(err))
            setPreviewItems([])
        } finally {
            setPreviewLoading(false)
        }
    }, [])

    useEffect(() => {
        if (useMaster && masterListId) {
            loadPreview(masterListId)
        } else {
            setPreviewItems([])
        }
    }, [useMaster, masterListId, loadPreview])

    const handleTemplateChange = (_, { value }) => {
        setTemplateId(value)
        loadTemplateQuestions(value)
    }

    const handleQuestionChange = (_, { value }) => {
        setSelectedQuestionId(value)
        const q = questions.find(x => x.questionId === value)
        applyQuestionSelection(q)
    }

    const handleMasterToggle = async checked => {
        if (!selectedQuestion) return
        setAssigning(true)
        try {
            if (checked) {
                const listId =
                    masterListId ||
                    masterLists[0]?.id ||
                    null
                if (!listId) {
                    errorToastRef.current(
                        'Create a master list first in the Master Lists tab',
                    )
                    return
                }
                await MasterAnswersService.assignMasterListToQuestion(
                    selectedQuestion.questionId,
                    listId,
                )
                setUseMaster(true)
                setMasterListId(listId)
                setQuestions(prev =>
                    prev.map(q =>
                        q.questionId === selectedQuestion.questionId
                            ? { ...q, masterListId: listId }
                            : q,
                    ),
                )
                successToastRef.current('Assigned to master list')
            } else {
                await MasterAnswersService.unassignMasterListFromQuestion(
                    selectedQuestion.questionId,
                )
                setUseMaster(false)
                setQuestions(prev =>
                    prev.map(q =>
                        q.questionId === selectedQuestion.questionId
                            ? { ...q, masterListId: null }
                            : q,
                    ),
                )
                successToastRef.current('Reverted to inline options')
            }
        } catch (err) {
            errorToastRef.current(getApiErrorMessage(err))
        } finally {
            setAssigning(false)
        }
    }

    const handleMasterListChange = async (_, { value }) => {
        if (!selectedQuestion || !useMaster) {
            setMasterListId(value)
            return
        }
        setAssigning(true)
        try {
            await MasterAnswersService.assignMasterListToQuestion(
                selectedQuestion.questionId,
                value,
            )
            setMasterListId(value)
            setQuestions(prev =>
                prev.map(q =>
                    q.questionId === selectedQuestion.questionId
                        ? { ...q, masterListId: value }
                        : q,
                ),
            )
            successToastRef.current('Master list updated')
        } catch (err) {
            errorToastRef.current(getApiErrorMessage(err))
        } finally {
            setAssigning(false)
        }
    }

    const masterListOptions = masterLists.map(l => ({
        key: l.id,
        value: l.id,
        text: `${l.listName} (${l.listKey})`,
    }))

    const questionOptions = questions.map(q => ({
        key: q.questionId,
        value: q.questionId,
        text: `${q.sectionName ? `${q.sectionName} — ` : ''}${q.questionText}`,
    }))

    const inlineOptions = selectedQuestion
        ? formatInlineOptions(selectedQuestion.multiAnswers)
        : []

    return (
        <>
            <InfoBanner>
                <span>ℹ️</span>
                <div>
                    On a multi-choice question, choose between{' '}
                    <strong>inline options</strong> (today&apos;s behaviour —
                    text and score per option, per template) and{' '}
                    <strong>use master list</strong> (single source of truth —
                    items pulled from the master at audit-start time).
                </div>
            </InfoBanner>

            <Form style={{ marginBottom: 16 }}>
                <Form.Group widths="equal">
                    <Form.Field>
                        <label>Template</label>
                        <Dropdown
                            placeholder="Select template"
                            fluid
                            selection
                            search
                            loading={templatesLoading}
                            options={templates}
                            value={templateId}
                            onChange={handleTemplateChange}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Multi-choice question</label>
                        <Dropdown
                            placeholder={
                                templateId
                                    ? 'Select question'
                                    : 'Select a template first'
                            }
                            fluid
                            selection
                            search
                            disabled={!templateId || !questions.length}
                            options={questionOptions}
                            value={selectedQuestionId}
                            onChange={handleQuestionChange}
                        />
                    </Form.Field>
                </Form.Group>
            </Form>

            {(templatesLoading || templateLoading) && (
                <LoaderBar content="Loading template..." />
            )}

            {templateId && !templateLoading && questions.length === 0 && (
                <EmptyHint>
                    <div className="empty-icon">❓</div>
                    <div style={{ fontWeight: 500, color: '#1f2937' }}>
                        No multi-choice questions
                    </div>
                    <div>
                        This template has no questions that can use a master
                        answer list.
                    </div>
                </EmptyHint>
            )}

            {selectedQuestion && (
                <TemplateQuestionCard>
                    <TqHeader>
                        <TqTypeBadge>Multi-choice</TqTypeBadge>
                        <TqName>{selectedQuestion.questionText}</TqName>
                        <span
                            style={{
                                fontSize: 11.5,
                                color: '#6b7280',
                            }}
                        >
                            QuestionId: {String(selectedQuestion.questionId).slice(0, 12)}…
                        </span>
                    </TqHeader>

                    <SettingsCard>
                        <SettingsCardHeader>
                            Question display settings
                        </SettingsCardHeader>
                        <SettingsCardBody>
                            <label style={{ display: 'flex', gap: 6 }}>
                                <input
                                    type="radio"
                                    name="displayType"
                                    checked={displayType === 'radio'}
                                    onChange={() => setDisplayType('radio')}
                                />
                                Radio Button
                            </label>
                            <label style={{ display: 'flex', gap: 6 }}>
                                <input
                                    type="radio"
                                    name="displayType"
                                    checked={displayType === 'listbox'}
                                    onChange={() => setDisplayType('listbox')}
                                />
                                List Box
                            </label>
                            <label
                                style={{
                                    display: 'flex',
                                    gap: 6,
                                    marginLeft: 12,
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={allowOther}
                                    onChange={e =>
                                        setAllowOther(e.target.checked)
                                    }
                                />
                                Allow Other Response
                            </label>
                            <span
                                style={{
                                    marginLeft: 'auto',
                                    fontSize: 12,
                                    color: '#6b7280',
                                }}
                            >
                                These live on the question, not the master
                                list.
                            </span>
                        </SettingsCardBody>
                    </SettingsCard>

                    <SourceToggle $active={useMaster}>
                        <ToggleSwitch>
                            <input
                                type="checkbox"
                                checked={useMaster}
                                disabled={assigning || listsLoading}
                                onChange={e =>
                                    handleMasterToggle(e.target.checked)
                                }
                            />
                            <span className="slider" />
                        </ToggleSwitch>
                        <div>
                            <div
                                style={{
                                    fontWeight: 600,
                                    fontSize: 13.5,
                                }}
                            >
                                {useMaster
                                    ? 'Use master list (single source of truth)'
                                    : 'Inline options (template-local)'}
                            </div>
                            <div
                                style={{
                                    fontSize: 12,
                                    color: '#6b7280',
                                    marginTop: 3,
                                }}
                            >
                                {useMaster
                                    ? 'Question pulls its options from the master at audit-start time. Editing the master updates every future audit.'
                                    : "Today's behaviour. Add/edit/score options per template. Changing them later requires editing each template."}
                            </div>
                        </div>
                    </SourceToggle>

                    {!useMaster && (
                        <>
                            <WarnBanner style={{ marginTop: 0 }}>
                                <span>⚠️</span>
                                <div>
                                    Inline options are edited in the template
                                    builder. Open this template to add or change
                                    per-template answer choices.
                                </div>
                            </WarnBanner>
                            <PreviewCard>
                                <PreviewTitle>
                                    Inline options on this question
                                </PreviewTitle>
                                {inlineOptions.length === 0 && (
                                    <div
                                        style={{
                                            fontSize: 13,
                                            color: '#6b7280',
                                        }}
                                    >
                                        No inline options defined.
                                    </div>
                                )}
                                {inlineOptions.map((opt, idx) => (
                                    <PreviewItem key={idx}>
                                        {opt.text || '(empty)'}
                                        <span className="score">
                                            Score: {opt.score}
                                        </span>
                                    </PreviewItem>
                                ))}
                            </PreviewCard>
                        </>
                    )}

                    {useMaster && (
                        <>
                            <Form>
                                <Form.Field>
                                    <label>Master list</label>
                                    <Dropdown
                                        fluid
                                        selection
                                        loading={listsLoading}
                                        options={masterListOptions}
                                        value={masterListId}
                                        onChange={handleMasterListChange}
                                        disabled={assigning}
                                    />
                                </Form.Field>
                            </Form>
                            {previewLoading && (
                                <LoaderBar content="Loading preview..." />
                            )}
                            <PreviewCard>
                                <PreviewTitle>
                                    Preview — items pulled from the master at
                                    audit-start time
                                </PreviewTitle>
                                {previewItems.length === 0 &&
                                    !previewLoading && (
                                        <div
                                            style={{
                                                fontSize: 13,
                                                color: '#6b7280',
                                            }}
                                        >
                                            No active items in this list.
                                        </div>
                                    )}
                                {previewItems.map(item => (
                                    <PreviewItem key={item.id}>
                                        {item.displayText || '(empty)'}
                                        <span className="score">
                                            Score: {item.score ?? 0}
                                        </span>
                                    </PreviewItem>
                                ))}
                            </PreviewCard>
                            <p
                                style={{
                                    fontSize: 12.5,
                                    color: '#6b7280',
                                    marginTop: 8,
                                }}
                            >
                                Items here are read-only. Manage them in the{' '}
                                <strong>Master Lists</strong> tab.
                            </p>
                        </>
                    )}
                </TemplateQuestionCard>
            )}
        </>
    )
}
